import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, X, ChevronLeft, ChevronRight, Sparkles,
         MessageSquare, BarChart3, UserCircle, ShieldCheck, PlayCircle,
         AlertTriangle, Target, Flag, FileDown } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';
import { useChat } from '../hooks/useChat';
import GoalChart from './GoalChart';

// A real worked example (verified numbers) shown live INSIDE the tour, so the user sees an
// actual answer + chart, not just a description of one.
const EX_COLUMNS = ['المنطقة', 'عدد القضايا'];
const EX_ROWS = [
  ['غير محدد', 5012], ['القطاع الغربي', 231], ['القطاع الجنوبي', 201],
  ['القطاع الأوسط', 110], ['القطاع الشرقي', 44],
];

/**
 * Outcome-framed product tour. It NAVIGATES the app (switches tabs) so the user sees each area
 * live behind a light scrim, frames every step as a job-to-be-done, and ends by running a REAL
 * query ("جرّب بنفسك") so they go from watching to doing. Big, dynamic, bilingual. Auto-runs once
 * on first login; replayable from the floating "?" button.
 */

const STORAGE_KEY = 'sera_tour_done_v5';
const TRY_QUESTION = 'أين تتركز شكاوى الهيئة حسب المنطقة؟';

const STEPS = [
  {
    tab: 'chat', icon: Sparkles,
    title_ar: 'حوّل بياناتك إلى قرارات', title_en: 'Turn your data into decisions',
    body_ar: 'اسأل عن عمليات الهيئة بلغتك الطبيعية واحصل على إجابات فورية — بدون انتظار فريق التقارير. جولة سريعة لما يمكنك إنجازه.',
    body_en: 'Ask about operations in plain language and get instant answers — no waiting on the reporting team. A quick tour of what you can get done.',
  },
  {
    tab: 'chat', icon: MessageSquare,
    title_ar: 'اسأل أي سؤال — احصل على إجابة ورسم', title_en: 'Ask anything — get an answer + chart',
    body_ar: 'اكتب سؤالك وتظهر إجابة واضحة مع رسم. مثال حقيقي:',
    body_en: 'Type a question and get a clear answer with a chart. A real example:',
    example: true,
  },
  {
    tab: 'dashboard', icon: BarChart3,
    title_ar: 'نظرة عامة فورية — وكل رسم باب للتعمّق', title_en: 'Instant overview — every chart is a door',
    body_ar: 'لوحة المعلومات تعطيك صورة الوضع بلمحة، وكل عمود قابل للنقر للتعمّق. الطريقة بالضبط:',
    body_en: 'The dashboard gives you the picture at a glance, and every bar is clickable to drill in. Exactly how:',
    steps: [
      { ar: 'مرّر المؤشر على أي عمود', en: 'Hover the cursor over any bar' },
      { ar: 'يُبرز العمود وتبهت بقية الأعمدة', en: 'That bar pops, the others dim' },
      { ar: 'انقر العمود', en: 'Click the bar' },
      { ar: 'تفتح محادثة تتعمّق في ذلك العمود تحديداً', en: 'A conversation opens, drilling into that exact bar' },
    ],
    drillExample: true,
  },
  {
    tab: 'dashboard', icon: FileDown,
    title_ar: 'صدّر تقريراً جاهزاً للمشاركة', title_en: 'Export a share-ready report',
    body_ar: 'حوّل اللوحة إلى تقرير PDF احترافي بهوية الهيئة — أو صدّر البيانات كـ CSV — بنقرة واحدة. الطريقة بالضبط:',
    body_en: 'Turn the dashboard into a branded, professional PDF report — or export the data as CSV — in one click. Exactly how:',
    steps: [
      { ar: 'في أعلى اللوحة اضغط زر «تصدير»', en: 'At the top of the dashboard click the "Export" button' },
      { ar: 'اختر الرسوم التي تريدها — وأي رسوم مثبّتة من محادثاتك', en: 'Pick the charts you want — plus any charts you pinned from chats' },
      { ar: 'اختر PDF (تقرير منسّق) أو CSV (بيانات خام)', en: 'Choose PDF (a formatted report) or CSV (raw data)' },
      { ar: 'يُنزَّل التقرير فوراً جاهزاً للعرض أو المشاركة', en: 'The report downloads instantly, ready to present or share' },
    ],
  },
  {
    tab: 'account360', icon: UserCircle,
    title_ar: 'العميل كاملاً في مكان واحد', title_en: 'The whole customer, in one place',
    body_ar: 'حساب ٣٦٠ يجمع نشاط الحساب الواحد عبر الأنظمة — شكاوى مقدم الخدمة وطلبات الخدمة والانقطاعات وشكاوى الهيئة — لتفهم العميل دفعة واحدة.',
    body_en: 'Account 360 brings one account’s activity together across systems — complaints, requests, outages, cases — so you understand a customer at once.',
  },
  {
    tab: 'chat', icon: Target,
    title_ar: 'من هدف إلى قرار — جرّب «وضع الهدف»', title_en: 'From goal to decision — try "Goal Mode"',
    body_ar: 'بدل سؤال واحد، صِف هدفاً للقرار فيخطّط النظام له ويكتب خلاصة صادقة — وأنت تراجع كل خطوة. الطريقة بالضبط:',
    body_en: 'Instead of one question, describe a decision goal — it plans it and writes an honest brief, and you review every step. Exactly how:',
    steps: [
      { ar: 'في الصفحة الرئيسية اختر «وضع الهدف»', en: 'On the home screen pick "Goal Mode"' },
      { ar: 'اكتب هدفك واضغط «حلّل الهدف»', en: 'Type your goal and hit "Analyze goal"' },
      { ar: 'يقترح النظام أسئلة فرعية — راجِعها أو عدّلها أو أضِف', en: 'It proposes sub-questions — review, edit, or add' },
      { ar: 'اعتمد، فيكتب خلاصة قرار تصارحك بحدود البيانات', en: 'Approve, and it writes a brief honest about the data’s limits' },
    ],
    goalExample: true,
  },
  {
    tab: 'chat', icon: Flag,
    title_ar: 'لاحظتَ خطأً؟ بلِّغ بنقرة', title_en: 'Spotted something off? Flag it',
    body_ar: 'رأيُك يحسّن النظام وفريقنا يراجع كل بلاغ. طريقتان:',
    body_en: 'Your feedback improves the system, and our team reviews every report. Two ways:',
    steps: [
      { ar: 'على أي إجابة: اضغط أيقونة العلَم 🚩 ← «الإبلاغ عن مشكلة» ← صِف المشكلة وأرسِل', en: 'On any answer: click the flag 🚩 → "Report issue" → describe it and submit' },
      { ar: 'في «وضع الهدف»: اضغط 👍 أو 👎 بجانب كل سؤال مقترح لتقييمه', en: 'In Goal Mode: tap 👍 or 👎 next to each proposed question to rate it' },
    ],
  },
  {
    tab: 'chat', icon: ShieldCheck,
    title_ar: 'قرارات قابلة للدفاع عنها — جرّبها الآن', title_en: 'Decisions you can defend — try it now',
    body_ar: 'النظام يصارحك بحدود البيانات ويُظهر الاستعلام خلف كل رقم — شفافية كاملة تجعل القرار قابلاً للتدقيق. جاهز؟ لنشغّل سؤالاً حقيقياً.',
    body_en: 'It tells you the data’s limits up front and shows the query behind every number — full, auditable transparency. Ready? Let’s run a real question.',
    isLast: true,
  },
];

const Scrim = styled(motion.div)`
  position: fixed; inset: 0; z-index: 9998;
  background: rgba(15, 23, 42, 0.38);
  box-shadow: inset 0 0 0 3px rgba(27,140,60,0.55);
`;

const Card = styled(motion.div)`
  position: fixed; z-index: 9999;
  bottom: 38px; left: 50%;
  width: min(600px, calc(100vw - 32px));
  max-height: calc(100vh - 76px); overflow-y: auto;
  background: #fff; border-radius: 22px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.32);
  border: 1px solid #e2e8f0;
  padding: 30px 32px 24px;
  direction: ${p => (p.$rtl ? 'rtl' : 'ltr')};
  text-align: ${p => (p.$rtl ? 'right' : 'left')};
`;

// Embedded worked example (chart + answer + caveat) shown live inside a tour step.
const ExBox = styled.div`
  margin: 14px 0 2px; border: 1px solid #e2e8f0; border-radius: 14px; overflow: hidden;
`;
const ExHead = styled.div`
  display: flex; align-items: center; gap: 7px; padding: 9px 13px; font-size: 13px; font-weight: 800; color: #006200;
  background: linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%); border-bottom: 1px solid rgba(27,140,60,0.18);
`;
const ExBody = styled.div` padding: 13px 15px; `;
const ExAnswer = styled.div` font-size: 14px; line-height: 1.7; color: #0f172a; font-weight: 600; margin-bottom: 10px; `;
const ExCaveat = styled.div`
  display: flex; gap: 7px; margin-top: 10px; padding: 9px 12px; border-radius: 10px;
  background: #fffbeb; border: 1px solid #fde68a; color: #92400e; font-size: 12px; line-height: 1.55;
  svg { flex-shrink: 0; margin-top: 1px; }
`;
// Goal-step worked example: goal → proposed sub-questions → honest brief.
const GoalLine = styled.div` font-size: 14px; font-weight: 800; color: #0f172a; margin-bottom: 10px; `;
const QChips = styled.div` display: flex; flex-wrap: wrap; gap: 7px; margin-bottom: 11px; `;
const QChip = styled.span`
  font-size: 12.5px; font-weight: 700; color: #006200; padding: 5px 11px; border-radius: 999px;
  background: #f0fdfa; border: 1px solid rgba(27,140,60,0.3);
`;
// Drill-step illustration: one bar "hovered" (full) while the rest dim — mirrors the live chart cue.
const MiniBars = styled.div` display: flex; align-items: flex-end; gap: 9px; height: 78px; margin: 4px 0 11px; `;
const MiniBar = styled.div`
  flex: 1; border-radius: 6px 6px 0 0; transition: opacity .2s;
  background: ${p => p.$color || '#1B8C3C'};
  opacity: ${p => (p.$dim ? 0.32 : 1)};
  box-shadow: ${p => (p.$dim ? 'none' : '0 0 0 2px rgba(27,140,60,0.35)')};
`;
const DrillHint = styled.div` font-size: 12.5px; line-height: 1.6; color: #475569; font-weight: 600; `;
// Numbered "exactly how it works" sequence (the real clicks, in order).
const Steps = styled.ol` margin: 14px 0 2px; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 9px; `;
const StepRow = styled.li` display: flex; align-items: flex-start; gap: 10px; font-size: 13.5px; line-height: 1.5; color: #334155; font-weight: 600; `;
const StepNum = styled.span`
  flex-shrink: 0; width: 23px; height: 23px; border-radius: 999px; margin-top: 1px;
  background: linear-gradient(135deg, #1B8C3C 0%, #0ea5e9 100%); color: #fff;
  font-size: 12px; font-weight: 800; display: flex; align-items: center; justify-content: center;
`;

const IconWrap = styled(motion.div)`
  width: 58px; height: 58px; border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #1B8C3C 0%, #0ea5e9 100%);
  color: #fff; margin-bottom: 16px;
  box-shadow: 0 8px 22px rgba(14,165,233,0.35);
`;
const Counter = styled.div`
  font-size: 12.5px; font-weight: 800; color: #94a3b8; letter-spacing: 0.5px; margin-bottom: 6px;
`;
const Title = styled.div` font-size: 23px; font-weight: 800; color: #0f172a; line-height: 1.4; margin-bottom: 12px; `;
const Body = styled.div` font-size: 16px; line-height: 1.85; color: #475569; `;

const Close = styled.button`
  position: absolute; top: 18px; ${p => (p.$rtl ? 'left: 18px;' : 'right: 18px;')}
  background: none; border: none; cursor: pointer; color: #94a3b8; display: flex; padding: 5px; border-radius: 9px;
  &:hover { background: #f1f5f9; color: #475569; }
`;
const Footer = styled.div` display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 24px; `;
const Dots = styled.div` display: flex; gap: 7px; `;
const Dot = styled.div`
  width: ${p => (p.$on ? '22px' : '8px')}; height: 8px; border-radius: 999px;
  background: ${p => (p.$on ? '#1B8C3C' : '#cbd5e1')}; transition: all .25s;
`;
const Btns = styled.div` display: flex; gap: 9px; `;
const Btn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 12px 20px; border-radius: 12px; font-size: 15px; font-weight: 800; cursor: pointer;
  border: 1px solid ${p => (p.$primary ? 'transparent' : '#e2e8f0')};
  background: ${p => (p.$primary ? '#1B8C3C' : '#fff')};
  color: ${p => (p.$primary ? '#fff' : '#475569')};
  &:hover { filter: brightness(0.97); }
`;
const SkipBtn = styled.button`
  background: none; border: none; cursor: pointer; color: #94a3b8; font-size: 13px; font-weight: 700; padding: 8px 4px;
  &:hover { color: #475569; }
`;
const Fab = styled(motion.button)`
  position: fixed; z-index: 9000; bottom: 22px; ${p => (p.$rtl ? 'left: 22px;' : 'right: 22px;')}
  width: 48px; height: 48px; border-radius: 999px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  background: #006200; color: #fff; border: none; box-shadow: 0 6px 18px rgba(13,118,110,0.42);
`;

function TourGuide({ activeTab, setActiveTab }) {
  const { language, isRTL } = useSettings();
  const chat = useChat(); // TourGuide is always rendered inside ChatProvider (see App.js)
  const rtl = isRTL || language === 'ar';
  const [running, setRunning] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      const t = setTimeout(() => setRunning(true), 700);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    if (running && STEPS[i] && setActiveTab) setActiveTab(STEPS[i].tab);
  }, [running, i, setActiveTab]);

  const finish = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, '1');
    setRunning(false); setI(0);
  }, []);

  const start = () => { setI(0); setRunning(true); };

  const tryIt = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setRunning(false); setI(0);
    if (setActiveTab) setActiveTab('chat');
    if (chat && chat.actions && chat.actions.sendMessage) chat.actions.sendMessage(TRY_QUESTION);
  };

  const step = STEPS[i];
  const Icon = step ? step.icon : Sparkles;
  const t = (ar, en) => (language === 'ar' ? ar : en);

  return (
    <>
      <Fab $rtl={rtl} onClick={start} whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }}
           title={t('جولة تعريفية', 'Take the tour')} aria-label="tour">
        <HelpCircle size={24} />
      </Fab>

      <AnimatePresence>
        {running && step && (
          <>
            <Scrim initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={finish} />
            <Card key={i} $rtl={rtl}
                  initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 30, scale: 0.96 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                  style={{ transform: 'translateX(-50%)' }}>
              <Close $rtl={rtl} onClick={finish}><X size={20} /></Close>
              <IconWrap animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}>
                <Icon size={28} />
              </IconWrap>
              <Counter>{t(`الخطوة ${i + 1} من ${STEPS.length}`, `STEP ${i + 1} OF ${STEPS.length}`)}</Counter>
              <Title>{t(step.title_ar, step.title_en)}</Title>
              <Body>{t(step.body_ar, step.body_en)}</Body>

              {step.steps && (
                <Steps>
                  {step.steps.map((s, k) => (
                    <StepRow key={k}><StepNum>{k + 1}</StepNum><span>{t(s.ar, s.en)}</span></StepRow>
                  ))}
                </Steps>
              )}

              {step.example && (
                <ExBox>
                  <ExHead><Target size={14} /> {t('أين تتركز شكاوى الهيئة حسب المنطقة؟', 'Where do regulatory cases concentrate by region?')}</ExHead>
                  <ExBody>
                    <ExAnswer>
                      {t('إجمالي 5,598 قضية. الغالبية غير مصنّفة جغرافياً (5,012)، ثم القطاع الغربي (231).',
                         'Total 5,598 cases. Most are unclassified by region (5,012), then Western (231).')}
                    </ExAnswer>
                    <GoalChart columnNames={EX_COLUMNS} results={EX_ROWS} big />
                    <ExCaveat>
                      <AlertTriangle size={14} />
                      {t('~90٪ من القضايا بلا منطقة مسجّلة — لا تُقرأ «الغربي الأعلى» كصورة كاملة.',
                         '~90% of cases have no region recorded — don’t read "Western leads" as the whole picture.')}
                    </ExCaveat>
                  </ExBody>
                </ExBox>
              )}

              {step.drillExample && (
                <ExBox>
                  <ExHead><BarChart3 size={14} /> {t('مرّر ثم انقر', 'Hover, then click')}</ExHead>
                  <ExBody>
                    <MiniBars>
                      <MiniBar $dim $color="#0ea5e9" style={{ height: '64%' }} />
                      <MiniBar $color="#1B8C3C" style={{ height: '100%' }} />
                      <MiniBar $dim $color="#6366f1" style={{ height: '52%' }} />
                      <MiniBar $dim $color="#f59e0b" style={{ height: '38%' }} />
                    </MiniBars>
                    <DrillHint>
                      {t('مرّر المؤشر ← يُبرز العمود وتبهت البقية. انقره ← يفتح محادثة تتعمّق في ذلك العمود.',
                         'Hover → the bar pops, the rest dim. Click it → opens a conversation drilling into that bar.')}
                    </DrillHint>
                  </ExBody>
                </ExBox>
              )}

              {step.goalExample && (
                <ExBox>
                  <ExHead><Target size={14} /> {t('مثال: من هدف إلى خلاصة', 'Example: goal → brief')}</ExHead>
                  <ExBody>
                    <GoalLine>{t('🎯 الهدف: «أين أركّز موارد معالجة شكاوى مقدم الخدمة؟»', '🎯 Goal: "Where should I focus complaint-handling resources?"')}</GoalLine>
                    <QChips>
                      <QChip>{t('① شكاوى مقدم الخدمة حسب المنطقة', '① Complaints by region')}</QChip>
                      <QChip>{t('② شكاوى مقدم الخدمة حسب الفئة', '② Complaints by category')}</QChip>
                    </QChips>
                    <ExAnswer>
                      {t('📋 الخلاصة: ركّز على المنطقة الشرقية (الأعلى شكاوى)، تليها الجنوبية.',
                         '📋 Brief: focus on Eastern (most complaints), then Southern.')}
                    </ExAnswer>
                    <ExCaveat>
                      <AlertTriangle size={14} />
                      {t('مع الإفصاح عن أي حدود في البيانات قبل القرار.',
                         'With any data limits disclosed up front, before you decide.')}
                    </ExCaveat>
                  </ExBody>
                </ExBox>
              )}

              <Footer>
                <Dots>{STEPS.map((_, k) => <Dot key={k} $on={k === i} />)}</Dots>
                <Btns>
                  {i > 0 && (
                    <Btn onClick={() => setI(i - 1)}>
                      {rtl ? <ChevronRight size={17} /> : <ChevronLeft size={17} />}{t('السابق', 'Back')}
                    </Btn>
                  )}
                  {!step.isLast ? (
                    <Btn $primary onClick={() => setI(i + 1)}>
                      {t('التالي', 'Next')}{rtl ? <ChevronLeft size={17} /> : <ChevronRight size={17} />}
                    </Btn>
                  ) : (
                    <Btn $primary onClick={tryIt}><PlayCircle size={18} />{t('جرّب بنفسك', 'Try it')}</Btn>
                  )}
                </Btns>
              </Footer>
              {!step.isLast && (
                <div style={{ textAlign: rtl ? 'left' : 'right', marginTop: 8 }}>
                  <SkipBtn onClick={finish}>{t('تخطّي الجولة', 'Skip tour')}</SkipBtn>
                </div>
              )}
            </Card>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default TourGuide;
